/**
 * The ★ overlay's glyphs (design v3 `glyphInner` / `die`): each special-rules
 * set drawn as it appears in its map's art (cross, starburst, mesa octagon,
 * keep, flag, …), the fact icons used in legend chips (sword, shield, link,
 * reach, one-way, cap, none, cards), and an N-sided die with N on it. All on a
 * 24 × 24 grid. Decorative: rows and chips carry their own labels.
 */
import React from 'react'
import Svg, { Circle, G, Line, Path, Polygon, Rect } from 'react-native-svg'
import type { ChipGlyph } from '../../../lib/ruleOverlayModel'

function starPoints(n: number, r1: number, r2: number): string {
  const p: string[] = []
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 ? r2 : r1
    const a = (Math.PI * i) / n - Math.PI / 2
    p.push(`${(12 + r * Math.cos(a)).toFixed(2)},${(12 + r * Math.sin(a)).toFixed(2)}`)
  }
  return p.join(' ')
}

function ngon(n: number, r: number, cx = 12, cy = 12, rot = -Math.PI / 2): string {
  const p: string[] = []
  for (let i = 0; i < n; i++) {
    const a = rot + (2 * Math.PI * i) / n
    p.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`)
  }
  return p.join(' ')
}

const STAR13 = starPoints(13, 11, 7.6)
const STAR5_SMALL = starPoints(5, 4.5, 2)
const STAR5 = starPoints(5, 10, 4.2)
const OCTAGON = ngon(8, 9.6, 12, 12, Math.PI / 8)
const HEX = ngon(6, 9.5, 12, 12, 0)

/** The glyph's shapes, for a parent <Svg viewBox="0 0 24 24">. */
export function GlyphShapes({ glyph, color }: { glyph: ChipGlyph; color: string }) {
  const line = { stroke: color, strokeWidth: 2, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  switch (glyph) {
    case 'cross':
      return <Path d="M9 3h6v6h6v6h-6v6H9v-6H3V9h6z" fill="#fff" stroke={color} strokeWidth={2.6} strokeLinejoin="round" />
    case 'starburst':
      return <Polygon points={STAR13} fill="#f2f7f8" stroke={color} strokeWidth={1.7} strokeLinejoin="round" />
    case 'octagon':
      return (
        <G>
          <Polygon points={OCTAGON} fill="#f2f2ef" stroke={color} strokeWidth={3.2} strokeLinejoin="round" />
          {Array.from({ length: 8 }, (_, i) => {
            const a = Math.PI / 8 + (Math.PI * i) / 4
            return <Line key={i} x1={12} y1={12} x2={12 + 8 * Math.cos(a)} y2={12 + 8 * Math.sin(a)} stroke="#333" strokeWidth={0.9} />
          })}
          <Circle cx={12} cy={12} r={2.3} fill="#f2f2ef" stroke="#333" strokeWidth={0.9} />
        </G>
      )
    case 'swatch':
      return (
        <G>
          <Rect x={4} y={4} width={16} height={16} rx={4} fill={color} stroke="#333" strokeWidth={1.2} />
          <Path d="M4 13c4-3 8 2 16-2" stroke="#333" strokeWidth={1.1} fill="none" />
        </G>
      )
    case 'grass':
      // Open field: a tuft of grass on the ground, always olive (the plains'
      // own grey would vanish on the paper chips).
      return <Path d="M3.5 19.5h17M7 19.5c-.3-3.6-1.6-6.4-3.4-8.6M9.6 19.5c0-4.7 1.2-8.6 3.4-11.6M12.6 19.5c.6-3.8 2.4-6.6 4.8-8.2M15.6 19.5c.9-2.4 2.4-4.2 4.4-5.2" stroke="#5b7a2a" strokeWidth={2.1} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    case 'tower': return <Path d="M8 21l2.2-11h3.6L16 21M7 10h10l-2.5-4.5h-5zM12 2.5v3M9 15h6" {...line} />
    case 'keep': return <Path d="M4 21V9h3V6h2.5v3h1.5V6h2v3h1.5V6H17v3h3v12zM10 21v-5h4v5" {...line} />
    case 'flag': return <Path d="M6.5 21V3M6.5 4h11l-3 4 3 4h-11" {...line} />
    case 'gate': return <Path d="M5 21V10a7 7 0 0 1 14 0v11zM9.5 21v-6.5h5V21" {...line} />
    case 'wall': return <Path d="M3 20V10h3V7h3v3h2V7h2v3h2V7h3v3h3v10zM3 15h18" {...line} />
    case 'cannon':
      return (
        <G>
          <Circle cx={8} cy={16.5} r={4} {...line} />
          <Path d="M10.5 13.5L20 7.5l1.6 2.6-9.3 6" {...line} />
        </G>
      )
    case 'island':
      return <Path d="M3 15c1-5 6-8 10-7s8 2 8 6-5 6-9 6-10 0-9-5z" fill={color} fillOpacity={0.35} stroke={color} strokeWidth={2} />
    case 'landing': return <Path d="M3 12h12M11 7l5 5-5 5M20 4v16" {...line} />
    case 'islet':
      return (
        <G>
          <Path d="M6 16l3-7 4 2 3-4 3 9z" fill={color} fillOpacity={0.35} stroke={color} strokeWidth={2} strokeLinejoin="round" />
          <Path d="M3 20c2-1.5 4 1.5 6 0s4 1.5 6 0 4 1.5 6 0" {...line} strokeWidth={1.5} />
        </G>
      )
    case 'ring': return <Circle cx={12} cy={12} r={8} {...line} strokeWidth={3} />
    case 'sand':
      return (
        <G>
          <Circle cx={12} cy={12} r={9} fill={color} fillOpacity={0.35} stroke={color} strokeWidth={2} />
          <Circle cx={9} cy={10} r={1} fill={color} />
          <Circle cx={14} cy={9} r={1} fill={color} />
          <Circle cx={12} cy={14.5} r={1} fill={color} />
          <Circle cx={16} cy={14} r={1} fill={color} />
        </G>
      )
    case 'hex': return <Polygon points={HEX} fill={color} fillOpacity={0.3} stroke={color} strokeWidth={2.2} strokeLinejoin="round" />
    case 'hexcore':
      return (
        <G>
          <Polygon points={HEX} fill="#7a0000" stroke={color} strokeWidth={2.2} strokeLinejoin="round" />
          <Polygon points={STAR5_SMALL} fill="#fff" />
        </G>
      )
    case 'sword': return <Path d="M5 19L18 6M14.5 5H19v4.5M7.5 13.5l3 3M3.5 20.5l2-2" {...line} strokeWidth={2.2} />
    case 'shield': return <Path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z" {...line} strokeWidth={2.2} />
    case 'link': return <Path d="M10 14a4 4 0 0 1 0-5.7l2.2-2.2a4 4 0 0 1 5.7 5.7l-1.1 1.1M14 10a4 4 0 0 1 0 5.7l-2.2 2.2a4 4 0 0 1-5.7-5.7l1.1-1.1" {...line} strokeWidth={2.2} />
    case 'reach': return <Path d="M3 12h3M8.5 12h3M14 12h4M15 7.5l4.5 4.5-4.5 4.5" {...line} strokeWidth={2.2} />
    case 'oneway': return <Path d="M3 12h15M13 6.5l6 5.5-6 5.5" {...line} strokeWidth={2.4} />
    case 'cards':
      return (
        <G>
          <Rect x={4} y={6} width={10} height={14} rx={2} {...line} />
          <Rect x={10} y={3.5} width={10} height={14} rx={2} {...line} transform="rotate(12 15 10)" />
        </G>
      )
    case 'cap': return <Path d="M4 5h16M12 9v11M8 13l4-4 4 4" {...line} strokeWidth={2.2} />
    case 'none':
      return (
        <G>
          <Circle cx={12} cy={12} r={8} {...line} />
          <Path d="M6.5 17.5l11-11" {...line} />
        </G>
      )
    case 'clock':
      return (
        <G>
          <Circle cx={12} cy={12} r={8.5} {...line} />
          <Path d="M12 7.5V12l3 2" {...line} />
        </G>
      )
  }
  return <Polygon points={STAR5} fill={color} />
}

export function RuleGlyph({ glyph, color, size = 22 }: { glyph: ChipGlyph; color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" style={{ overflow: 'visible' }}>
      <GlyphShapes glyph={glyph} color={color} />
    </Svg>
  )
}

/** MaterialCommunityIcons paths (Apache-2.0) the board tags draw inside the SVG. */
export const TAG_ICON_PATHS: Record<string, string> = {
  plus: 'M18.98 12.98H12.98V18.98H11.01V12.98H5.01V11.01H11.01V5.01H12.98V11.01H18.98Z',
  'arrow-right-bold': 'M3.98 15.0V9.0H12.0V4.17L19.82 12.0L12.0 19.82V15.0Z',
  'alert-outline': 'M12.0 2.01 0.98 21.0H23.01ZM12.0 6.0 19.54 18.98H4.45ZM11.01 9.98V14.01H12.98V9.98ZM11.01 15.98V18.0H12.98V15.98Z',
  'trophy-outline': 'M18.0 2.01Q17.29 2.01 16.64 2.64Q15.98 3.28 15.98 3.98H8.01Q8.01 3.28 7.35 2.64Q6.70 2.01 6.0 2.01H2.01V11.01Q2.01 11.76 2.62 12.37Q3.23 12.98 3.98 12.98H6.18Q6.51 14.67 7.59 15.65Q8.81 16.78 11.01 17.01V19.07Q9.98 19.21 9.28 19.73Q8.71 20.10 8.39 20.71Q8.15 21.14 8.06 21.60L8.01 21.98H15.98L15.93 21.60Q15.84 21.14 15.60 20.71Q15.28 20.10 14.71 19.73Q14.01 19.21 12.98 19.07V17.01Q15.18 16.78 16.40 15.65Q17.48 14.67 17.81 12.98H20.01Q20.76 12.98 21.37 12.37Q21.98 11.76 21.98 11.01V2.01ZM6.0 11.01H3.98V3.98H6.0ZM15.98 11.48Q15.98 13.17 15.28 13.96Q14.34 15.0 12.0 15.0Q9.65 15.0 8.71 13.96Q8.01 13.17 8.01 11.48V6.0H15.98ZM20.01 11.01H18.0V3.98H20.01Z',
}
